// The changed files of pull requests as this browser last listed them,
// each with the head and base it was listed at (a runtime cache,
// runtime/cache.ts, in the signed-in account's IndexedDB): a reload at the
// same head opens its diff on them while GitHub is asked only whether the
// head moved (see `fetchFiles`). A listing of one head and base never
// changes on GitHub, but for its cut at MAX_FILES, which `fetchFiles`
// never keeps; so no write to the pull request makes one stale, and none
// is dropped for it.
//
// One listing per pull request, its latest: the head before a push is
// not opened again. It is read as the page opens (`keptFiles`), beside
// the pull request's own read, so that once the pull request names its
// head the listing is at hand at once and the files' read starts in the
// same step, ahead of what is read beside the pull request (an IndexedDB
// read takes long enough for those to take every request slot first).
//
// Listings hold their patches and can be large, so few are kept: the
// least recently used go first (a use writes its listing again when it was
// written longer ago than TOUCH_MS, so a listing read every day stays).
// The files' texts are kept apart, by blob, by the diff loader (api/gh.ts
// `blob`): with a kept listing, a file whose head blob is kept and whose
// patch GitHub gave diffs with no request (its base side is the head's
// with the patch taken back).
import type { PrFile } from '../../api/queries';
import { identityUser } from '../../api/identity';
import { cacheKey, hashInputs, openCache, type Cache } from '../../runtime/cache';
import { idbBackend } from '../../runtime/idb';
import type { FilesAt, KeptListings, PrAt } from './fetch';

/** How long a listing is kept unused, and how many are. */
export const FILES_KEPT_MS = 30 * 24 * 60 * 60_000;
export const FILES_KEPT = 16;
/** A use writes a listing again once it was written this long ago, which keeps it among the most recently used. */
export const TOUCH_MS = 24 * 60 * 60_000;

const STATUSES: readonly string[] = ['added', 'removed', 'modified', 'renamed', 'copied', 'changed', 'unchanged'] satisfies PrFile['status'][];

const optString = (x: unknown) => x === undefined || typeof x === 'string';

function isFile(x: unknown): x is PrFile {
  if (typeof x !== 'object' || x === null) return false;
  const f = x as Record<string, unknown>;
  return (
    typeof f.filename === 'string' &&
    STATUSES.includes(f.status as string) &&
    (f.sha === null || typeof f.sha === 'string') &&
    typeof f.additions === 'number' &&
    typeof f.deletions === 'number' &&
    typeof f.changes === 'number' &&
    optString(f.patch) &&
    optString(f.previous_filename)
  );
}

/** What is kept of a file: the fields the views read (GitHub's answer holds URLs besides). */
function lean(f: PrFile): PrFile {
  const out: PrFile = { sha: f.sha, filename: f.filename, status: f.status, additions: f.additions, deletions: f.deletions, changes: f.changes };
  if (f.patch !== undefined) out.patch = f.patch;
  if (f.previous_filename !== undefined) out.previous_filename = f.previous_filename;
  return out;
}

/** A kept listing: the head and base it is of, and its files. */
export interface KeptListing {
  head: string;
  base: string;
  files: PrFile[];
}

const isListing = (x: unknown): x is KeptListing => {
  if (typeof x !== 'object' || x === null) return false;
  const l = x as Record<string, unknown>;
  return typeof l.head === 'string' && typeof l.base === 'string' && Array.isArray(l.files) && l.files.every(isFile);
};

/** The cache of listings for `account` at `origin`, one per pull request, by `owner/repo#number`. */
export function filesCache(origin: string, account: string, backend = idbBackend(), now: () => number = Date.now): Cache<KeptListing> {
  return openCache<KeptListing>({
    name: 'pr-files',
    key: cacheKey({ origin, account, schemaVersion: 1, inputsHash: hashInputs() }),
    decode: (raw) => (isListing(raw) ? raw : null),
    backend,
    maxAgeMs: FILES_KEPT_MS,
    limit: FILES_KEPT,
    now,
  });
}

let shared: { account: string; cache: Cache<KeptListing> } | null = null;

/** The signed-in account's cache, held by a lease from its first use; null when nobody is signed in. */
function sharedFilesCache(): Cache<KeptListing> | null {
  const account = identityUser();
  if (!account || typeof location === 'undefined') return null;
  if (shared?.account !== account) {
    const cache = filesCache(location.origin, account);
    cache.hold();
    void cache.gc();
    shared = { account, cache };
  }
  return shared.cache;
}

/** What one cache holds of the pull request: the read under way, and once it is done, the listing (null: none) and when it was written. */
interface Held {
  cache: Cache<KeptListing>;
  read: Promise<void>;
  listing: KeptListing | null | undefined;
  written: number;
}

/**
 * The listing kept for the pull request `pr` (see the file comment), in
 * the cache of whoever is signed in at each use (`cache`): nothing is kept
 * while nobody is. Its read begins now, so `get` answers at once when it
 * is done by the time the head is known (else once it is).
 */
export function keptFiles(pr: PrAt, cache: () => Cache<KeptListing> | null = sharedFilesCache, now: () => number = Date.now): KeptListings {
  const key = `${pr.owner}/${pr.repo}`.toLowerCase() + `#${pr.number}`;
  let held: Held | null = null;
  /** What the signed-in account's cache holds of the pull request, its read begun on first use; null while nobody is signed in. */
  const hold = (): Held | null => {
    const c = cache();
    if (!c) return null;
    if (held?.cache !== c) {
      const h: Held = { cache: c, read: Promise.resolve(), listing: undefined, written: 0 };
      h.read = c.get(key).then(
        (e) => {
          // A listing kept meanwhile (`put`) is newer than the one read.
          if (h.listing !== undefined) return;
          h.listing = e?.value ?? null;
          h.written = e?.at ?? 0;
        },
        () => void (h.listing ??= null),
      );
      held = h;
    }
    return held;
  };
  const at = (l: KeptListing | null | undefined, x: FilesAt) => (l && l.head === x.head && l.base === x.base ? l.files : null);
  hold();
  return {
    get(x) {
      const h = hold();
      if (!h) return null;
      return h.listing !== undefined ? at(h.listing, x) : h.read.then(() => at(h.listing, x));
    },
    put(x, files) {
      const h = hold();
      if (!h) return;
      if (at(h.listing, x) && now() - h.written < TOUCH_MS) return;
      const listing: KeptListing = { head: x.head, base: x.base, files: files.map(lean) };
      h.listing = listing;
      h.written = now();
      // Kept in the background: a listing that could not be stored is listed from GitHub next time.
      void h.cache.write({ [key]: listing }, h.cache.begin()).catch(() => {});
    },
  };
}
